/**
 * Login screen — email + optional password, magic-link fallback,
 * forgot-password link, demo button on localhost.
 */
import { useState } from "preact/hooks";
import type { JSX } from "preact";
import { sb } from "../supabase";
import { ALLOWED_EMAIL_DOMAIN, IS_LOCALHOST } from "../config";
import { toast } from "../utils/toast";
import { enterDemoMode } from "./demo";
import { BgPattern, IconsLogo } from "../ui/Brand";

function validateEmail(email: string): string | null {
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return "That doesn't look like an email";
  if (!email.endsWith(`@${ALLOWED_EMAIL_DOMAIN}`)) return `Use your @${ALLOWED_EMAIL_DOMAIN} email`;
  return null;
}

export function Login(): JSX.Element {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [linkSentTo, setLinkSentTo] = useState<string | null>(null);
  const [forgotBusy, setForgotBusy] = useState(false);

  const onSubmit = async (e: Event): Promise<void> => {
    e.preventDefault();

    // Read the live DOM values via FormData rather than relying on React
    // state. This is critical on iOS Safari and some mobile Chrome
    // variants: the iOS keychain auto-fill populates `<input>.value`
    // but does NOT reliably fire `onInput`, so the controlled-input
    // state can lag. Submitting based on that stale state routes
    // autofilled credentials to the magic-link branch ("Check your
    // inbox") even though the password field is visually filled —
    // which was the "logs in fails, loops back to the link" report
    // from mobile users. FormData reads what's actually in the form
    // at the moment of submit, regardless of how it got there.
    const fd = new FormData(e.target as HTMLFormElement);
    const cleanEmail = String(fd.get("email") ?? "").trim().toLowerCase();
    const livePassword = String(fd.get("password") ?? "");

    // Keep React state in sync with what we actually submitted so
    // any subsequent re-render (error toast → user adjusts → retries)
    // shows the values they expect to see. The ESLint security rule
    // for timing attacks flags any `password !==` comparison; here
    // it's a state-sync check between two local strings, not a
    // credential compare — the real auth happens via Supabase below.
    if (email !== cleanEmail) setEmail(cleanEmail);
    // eslint-disable-next-line security/detect-possible-timing-attacks
    if (password !== livePassword) setPassword(livePassword);

    const err = validateEmail(cleanEmail);
    if (err) {
      toast(err);
      return;
    }
    setBusy(true);
    try {
      if (livePassword) {
        // Race against a 12s timeout so a hung connection can't trap the
        // UI on "Signing…". On success, onAuthStateChange replaces the
        // whole Login component anyway.
        const signInPromise = sb.auth.signInWithPassword({ email: cleanEmail, password: livePassword });
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("Sign-in timed out — check your connection.")), 12000),
        );
        const { error } = await Promise.race([signInPromise, timeoutPromise]);
        if (error) {
          // Don't leak which half is wrong
          toast(
            "Wrong email or password. If you haven't set a password yet, leave that field empty and we'll email you a sign-in link.",
          );
          setBusy(false);
          return;
        }
        // Success → onAuthStateChange handles boot. As a safety net, drop
        // the "Signing…" busy state after a short grace period in case the
        // listener somehow doesn't fire.
        setTimeout(() => setBusy(false), 4000);
      } else {
        const { error } = await sb.auth.signInWithOtp({
          email: cleanEmail,
          options: { emailRedirectTo: window.location.href.split("#")[0] },
        });
        if (error) throw error;
        setLinkSentTo(cleanEmail);
      }
    } catch (err) {
      console.error("sign-in failed:", err);
      const msg = err instanceof Error ? err.message : "Something went wrong";
      toast(msg);
      setBusy(false);
    }
  };

  const onForgot = async (e: Event): Promise<void> => {
    e.preventDefault();
    // Same autofill-evasion treatment as onSubmit: read the live DOM
    // value rather than the (possibly-stale) React state. The link
    // sits inside the form, so we go up to the form and pull email
    // via FormData. Falls back to React state if the form lookup
    // fails for any reason.
    const form = (e.currentTarget as HTMLElement | null)?.closest("form") ?? null;
    const fromDom = form
      ? String(new FormData(form).get("email") ?? "")
      : email;
    const cleanEmail = (fromDom || email).trim().toLowerCase();
    if (cleanEmail !== email) setEmail(cleanEmail);
    const err = validateEmail(cleanEmail);
    if (err) {
      toast(err);
      return;
    }
    setForgotBusy(true);
    try {
      const { error } = await sb.auth.resetPasswordForEmail(cleanEmail, {
        redirectTo: window.location.href.split("#")[0],
      });
      if (error) throw error;
      setLinkSentTo(cleanEmail);
    } catch (err) {
      console.error("recovery failed:", err);
      toast(err instanceof Error ? err.message : "Could not send recovery link");
      setForgotBusy(false);
    }
  };

  if (linkSentTo) {
    return (
      <div class="login-wrap">
        <BgPattern />
        <div class="login-card">
          <div class="login-logo">
            <IconsLogo className="lg" />
            <span class="tag">Innovation strategies</span>
          </div>
          <div class="kicker">Office presence</div>
          <h1>Check your inbox</h1>
          <p>
            We sent a single-use link to <strong>{linkSentTo}</strong>.
            <br />
            Click it on this device to enter the tracker. The link is valid for one hour.
          </p>
          <div class="login-hint">
            Wrong address? <a href="" onClick={(e) => { e.preventDefault(); setLinkSentTo(null); setBusy(false); }}>Start over</a>
          </div>
        </div>
      </div>
    );
  }

  const btnLabel = password ? "Sign in" : "Send me a sign-in link";

  return (
    <div class="login-wrap">
      <BgPattern />
      <div class="login-card">
        <div class="login-logo">
          <IconsLogo className="lg" />
          <span class="tag">Innovation strategies</span>
        </div>
        <div class="kicker">Office presence</div>
        <h1>Presence Tracker</h1>
        <p>Sign in with your corporate account to plan and share your weekly office presence.</p>
        <form onSubmit={onSubmit} novalidate>
          <label for="loginEmail">Corporate email</label>
          <input
            id="loginEmail"
            name="email"
            type="email"
            autocomplete="email"
            placeholder={`name.surname@${ALLOWED_EMAIL_DOMAIN}`}
            value={email}
            onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
            required
          />
          <label for="loginPassword" style="margin-top:14px">
            Password <span class="opt-tag">optional on first sign-in</span>
          </label>
          {/* name="password" is intentionally set so `new FormData(form)`
              in onSubmit can read the live value — required to dodge the
              iOS keychain auto-fill bug where the password is in the DOM
              but the controlled-input onInput never fires. Without this,
              autofill triggers the magic-link branch instead of signing
              in with the autofilled credentials. */}
          <input
            id="loginPassword"
            name="password"
            type="password"
            autocomplete="current-password"
            placeholder="Leave empty to receive a sign-in link"
            value={password}
            onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
          />
          <button type="submit" disabled={busy}>
            {busy ? "Signing in…" : btnLabel}
          </button>
          <div class="login-extra">
            <a href="" onClick={onForgot} aria-disabled={forgotBusy}>
              {forgotBusy ? "Sending recovery link…" : "Forgot your password?"}
            </a>
          </div>
          {IS_LOCALHOST && (
            <button
              type="button"
              class="demo-btn"
              onClick={() => void enterDemoMode()}
            >
              Skip sign-in &mdash; load sample data
            </button>
          )}
        </form>
        <div class="login-hint">
          Sign-in is restricted to <code>@{ALLOWED_EMAIL_DOMAIN}</code> addresses. After your first
          sign-in via email link, you can set a password to skip the inbox round-trip.
        </div>
        {IS_LOCALHOST && (
          <div class="dev-banner">
            <strong>Localhost dev mode.</strong> The dashed button loads a synthetic dataset that
            never touches Supabase, so you can preview the UI without an account.
          </div>
        )}
      </div>
    </div>
  );
}
